/**
 * UI utility functions for responsive design and other UI-related operations
 */
import { useWindowSize } from '@vueuse/core'
import { computed } from 'vue'

/**
 * Breakpoint definitions for responsive design
 */
export enum ScreenSize {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  XXL = 'xxl',
}

/**
 * Breakpoint width values in pixels
 */
export const BREAKPOINTS = {
  [ScreenSize.XS]: 0, // Extra small devices
  [ScreenSize.SM]: 576, // Small devices (phones)
  [ScreenSize.MD]: 768, // Medium devices (tablets)
  [ScreenSize.LG]: 992, // Large devices (laptops)
  [ScreenSize.XL]: 1200, // Extra large devices (large laptops/desktops)
  [ScreenSize.XXL]: 1400, // Extra extra large devices (wide screens)
}

/**
 * Gets the current screen size category based on viewport width
 *
 * @returns {ScreenSize} The current screen size category
 */
export const getCurrentScreenSize = (): ScreenSize => {
  const width = window.innerWidth

  if (width < BREAKPOINTS[ScreenSize.SM]) return ScreenSize.XS
  if (width < BREAKPOINTS[ScreenSize.MD]) return ScreenSize.SM
  if (width < BREAKPOINTS[ScreenSize.LG]) return ScreenSize.MD
  if (width < BREAKPOINTS[ScreenSize.XL]) return ScreenSize.LG
  if (width < BREAKPOINTS[ScreenSize.XXL]) return ScreenSize.XL
  return ScreenSize.XXL
}

/**
 * Checks if the current screen size is smaller than or equal to the specified size
 *
 * @param size The breakpoint to compare against
 * @returns {boolean} True if the current screen is smaller than or equal to the specified size
 */
export const isScreenSizeBelow = (size: ScreenSize): boolean => {
  const currentSize = getCurrentScreenSize()
  const breakpoints = Object.entries(BREAKPOINTS)
  const currentIndex = breakpoints.findIndex(([key]) => key === currentSize)
  const targetIndex = breakpoints.findIndex(([key]) => key === size)

  return currentIndex <= targetIndex
}

// Use @vueuse/core for reactive window size
const { width } = useWindowSize()

// Current screen size category (computed)
export const currentScreenSize = computed((): ScreenSize => {
  const currentWidth = width.value

  if (currentWidth < BREAKPOINTS[ScreenSize.SM]) return ScreenSize.XS
  if (currentWidth < BREAKPOINTS[ScreenSize.MD]) return ScreenSize.SM
  if (currentWidth < BREAKPOINTS[ScreenSize.LG]) return ScreenSize.MD
  if (currentWidth < BREAKPOINTS[ScreenSize.XL]) return ScreenSize.LG
  if (currentWidth < BREAKPOINTS[ScreenSize.XXL]) return ScreenSize.XL
  return ScreenSize.XXL
})

// Create reactive screen size variables based on current width
export const isSmallScreen = computed(() => {
  const size = currentScreenSize.value
  return size === ScreenSize.XS || size === ScreenSize.SM
})
export const isMediumScreen = computed(() => {
  const size = currentScreenSize.value
  return size === ScreenSize.XS || size === ScreenSize.SM || size === ScreenSize.MD
})
// Always true since it includes all sizes

/**
 * Detects if the current device is a touch device
 * Uses multiple detection methods for better accuracy across different browsers and devices
 *
 * @returns {boolean} True if the device supports touch input
 */
export const isTouchDevice = (): boolean => {
  // Check for touch capabilities
  if ('ontouchstart' in window) {
    return true
  }

  // Check for maximum touch points (modern approach)
  if (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) {
    return true
  }

  // Check for pointer capabilities (IE10+)
  interface NavigatorWithMSTouch extends Navigator {
    msMaxTouchPoints?: number
  }
  const nav = navigator as NavigatorWithMSTouch
  if (nav.msMaxTouchPoints && nav.msMaxTouchPoints > 0) {
    return true
  }

  // Check for touch media query
  if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
    return true
  }

  return false
}
